import React, {Fragment, useRef} from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Copy, ThumbsUp, ThumbsDown, ChevronDown, Check } from "react-feather";
import { ActionButton } from "./ActionButton";
import Tooltip from 'react-bootstrap/Tooltip';
import Overlay from 'react-bootstrap/Overlay';

/**
 * @typedef {import('@coveo/headless').GeneratedAnswerState} GeneratedAnswerState
 */

/**
 * @param {Object} props
 * @param {GeneratedAnswerState} props.coveoState
 */
const HeaderActions = ({
  coveoState,
  answerExpanded,
  answerCopied,
  onThumbsUpClick,
  onThumbsDownClick,
  onCopyAnswerClick,
  onExpandClick
}) => {
  const { isStreaming, liked, disliked } = coveoState;

  const browserHasClipboardCapability =
    typeof window.navigator !== "undefined" &&
    typeof window.navigator.clipboard !== "undefined" &&
    typeof window.navigator.clipboard.writeText === "function";

  const actionsRef = useRef(null);

  return (
    <div className="header-actions" ref={actionsRef}>
      <ActionButton
        title="Helpful"
        show={answerExpanded}
        onClick={onThumbsUpClick}
        disabled={isStreaming || liked}
      >
        <ThumbsUp
          color="gray"
          size={20}
          className={classNames({ "thumbs-up-active": liked })}
        />
      </ActionButton>
      <ActionButton
        title="Not Helpful"
        show={answerExpanded}
        onClick={onThumbsDownClick}
        disabled={isStreaming || disliked}
      >
        <ThumbsDown
          color="gray"
          size={20}
          className={classNames({ "thumbs-down-active": disliked })}
        />
      </ActionButton>
      {browserHasClipboardCapability && (
        <Fragment>
          <Overlay placement="top" target={actionsRef.current} show={answerCopied}>
            {(props) => (
              <Tooltip id="copied-tooltip" {...props}>
                Copied!
              </Tooltip>
            )}
          </Overlay>
          <ActionButton
            title="Copy Answer"
            show={answerExpanded}
            onClick={onCopyAnswerClick}
            disabled={isStreaming || answerCopied}
          >
            {answerCopied ? (
              <Check color="gray" size={20} />
            ) : (
              <Copy
                color="gray"
                size={20}
                className={classNames({ "copy-active": answerCopied })}
              />
            )}
          </ActionButton>
        </Fragment>
      )}
      <ActionButton
        title={answerExpanded ? "Collapse" : "Expand"}
        onClick={onExpandClick}
        aria-controls="gen-answer-content"
        aria-expanded={answerExpanded}
      >
        <ChevronDown
          color="gray"
          size={20}
          className={classNames({ "answer-expanded": answerExpanded })}
        />
      </ActionButton>
    </div>
  );
};

HeaderActions.propTypes = {
  coveoState: PropTypes.object,
  answerExpanded: PropTypes.bool,
  answerCopied: PropTypes.bool,
  onThumbsUpClick: PropTypes.func,
  onThumbsDownClick: PropTypes.func,
  onCopyAnswerClick: PropTypes.func,
  onExpandClick: PropTypes.func
};

export { HeaderActions };
